import styled from "styled-components";
import { Modal } from "react-bootstrap";
import { colors, types } from "../../../libs";

export const Container = styled.div`
  padding: 8px 12px 40px;
`;

export const Title = styled.h1`
  ${types.headers.secondary};
  background: ${colors.text.textGradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 0;
`;

export const Subtitle = styled.p`
  color: ${colors.text.gray};
  margin: 8px 0 24px;
`;

export const CurrentPlan = styled.section`
  background:
    radial-gradient(circle at 92% 10%, rgba(183, 248, 77, 0.11), transparent 32%),
    ${colors.background.lightBlack};
  border: 1px solid rgba(183, 248, 77, 0.28);
  border-radius: 16px;
  box-shadow: 0 18px 45px rgba(0, 0, 0, 0.22);
  padding: 22px;
`;

export const OverviewGrid = styled.div`
  align-items: stretch;
  display: grid;
  gap: 18px;
  grid-template-columns: minmax(0, 1.65fr) minmax(300px, 0.75fr);
  margin-bottom: 34px;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

export const PaymentMethodCard = styled.section<{ $issue?: boolean }>`
  background:
    radial-gradient(
      circle at 95% 5%,
      ${({ $issue }) => ($issue ? "rgba(220, 102, 120, 0.12)" : "rgba(183, 248, 77, 0.08)")},
      transparent 34%
    ),
    ${colors.background.lightBlack};
  border: 1px solid
    ${({ $issue }) => ($issue ? "rgba(220, 102, 120, 0.55)" : "rgba(255, 255, 255, 0.13)")};
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  min-height: 100%;
  padding: 22px;
`;

export const BillingCardVisual = styled.div`
  background: linear-gradient(145deg, #252a31, #15171b);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 13px;
  color: ${colors.text.white};
  margin: 18px 0 14px;
  padding: 18px;
`;

export const CardBrandRow = styled.div`
  align-items: center;
  color: ${colors.text.gray};
  display: flex;
  font-size: 12px;
  justify-content: space-between;
  text-transform: uppercase;
`;

export const CardNumber = styled.div`
  font-size: 20px;
  font-weight: 650;
  letter-spacing: 0.08em;
  margin: 20px 0 14px;
`;

export const CardMeta = styled.div`
  color: ${colors.text.lightGray};
  display: flex;
  font-size: 12px;
  justify-content: space-between;
`;

export const BillingAddress = styled.p`
  color: ${colors.text.gray};
  font-size: 12px;
  line-height: 1.5;
  margin: 0 0 18px;
`;

export const ManualBillingDetails = styled.div`
  background: rgba(183, 248, 77, 0.055);
  border: 1px solid rgba(183, 248, 77, 0.18);
  border-radius: 11px;
  display: grid;
  gap: 11px;
  margin: 18px 0 14px;
  padding: 15px;

  div {
    align-items: center;
    display: flex;
    gap: 14px;
    justify-content: space-between;
  }

  span {
    color: ${colors.text.gray};
    font-size: 11px;
  }

  strong {
    color: ${colors.text.white};
    font-size: 12px;
    text-align: right;
    text-transform: capitalize;
  }
`;

export const PaymentMethodNote = styled.p`
  background: rgba(255, 255, 255, 0.045);
  border-radius: 7px;
  color: ${colors.text.lightGray};
  font-size: 11px;
  line-height: 1.45;
  margin: -7px 0 16px;
  padding: 8px 10px;
`;

export const BillingActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 9px;
  margin-top: auto;
`;

export const BillingButton = styled.button<{ $secondary?: boolean }>`
  background: ${({ $secondary }) => ($secondary ? "transparent" : colors.text.green)};
  border: 1px solid
    ${({ $secondary }) => ($secondary ? "rgba(255, 255, 255, 0.22)" : colors.text.green)};
  border-radius: 8px;
  color: ${({ $secondary }) => ($secondary ? colors.text.white : "#17200e")};
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;
  min-height: 40px;
  padding: 9px 14px;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
`;

export const PlanHeader = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 20px;
  justify-content: space-between;

  @media (max-width: 650px) {
    flex-direction: column;
  }
`;

export const Eyebrow = styled.p`
  color: ${colors.text.green};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  margin: 0 0 5px;
  text-transform: uppercase;
`;

export const PlanName = styled.h2`
  color: ${colors.text.white};
  margin: 0 0 7px;
`;

export const Description = styled.p`
  color: ${colors.text.gray};
  margin: 0;
`;

export const Price = styled.div`
  color: ${colors.text.white};
  font-size: 27px;
  font-weight: 700;
  white-space: nowrap;

  span {
    color: ${colors.text.gray};
    font-size: 13px;
    font-weight: 400;
  }
`;

export const Features = styled.ul`
  color: ${colors.text.mediumWhite};
  display: grid;
  gap: 8px 28px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin: 20px 0 0;
  padding-left: 20px;

  @media (max-width: 650px) {
    grid-template-columns: 1fr;
  }
`;

export const PaymentWarning = styled.div`
  background: #342126;
  border: 1px solid #dc6678;
  border-radius: 8px;
  color: #ffb1bd;
  margin-top: 20px;
  padding: 12px 14px;
`;

export const ScheduledPlanNotice = styled.div`
  background: rgba(183, 248, 77, 0.08);
  border: 1px solid rgba(183, 248, 77, 0.4);
  border-radius: 8px;
  color: ${colors.text.mediumWhite};
  margin-top: 20px;
  padding: 12px 14px;

  strong {
    color: #b7f84d;
  }
`;

export const PlanFooter = styled.div`
  align-items: flex-end;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  gap: 18px;
  justify-content: space-between;
  margin-top: 22px;
  padding-top: 18px;

  @media (max-width: 650px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

export const NextBillingDate = styled.div`
  display: grid;
  gap: 2px;

  span,
  small {
    color: ${colors.text.gray};
    font-size: 11px;
  }

  strong {
    color: ${colors.text.white};
    font-size: 15px;
  }
`;

export const PlanActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 9px;
  justify-content: flex-end;
`;

export const PlanActionButton = styled.button<{ $danger?: boolean }>`
  background: ${({ $danger }) => ($danger ? "rgba(220, 102, 120, 0.08)" : "transparent")};
  border: 1px solid
    ${({ $danger }) => ($danger ? "rgba(220, 102, 120, 0.55)" : "rgba(255, 255, 255, 0.22)")};
  border-radius: 8px;
  color: ${({ $danger }) => ($danger ? "#ff9eac" : colors.text.white)};
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
  min-height: 38px;
  padding: 8px 13px;

  &:hover:not(:disabled) {
    background: ${({ $danger }) =>
      $danger ? "rgba(220, 102, 120, 0.17)" : "rgba(255, 255, 255, 0.07)"};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

export const SectionTitle = styled.h2`
  color: ${colors.text.white};
  margin: 0 0 6px;
`;

export const Cards = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-top: 20px;

  .package-card {
    background:
      linear-gradient(145deg, rgba(255, 255, 255, 0.045), rgba(255, 255, 255, 0.01)),
      ${colors.background.lightBlack};
    border: 1px solid rgba(255, 255, 255, 0.13);
    border-radius: 16px;
    box-shadow: 0 16px 36px rgba(0, 0, 0, 0.22);
    color: ${colors.text.gray};
    display: flex;
    flex-direction: column;
    height: 100%;
    margin: 0;
    max-width: none;
    overflow: hidden;
    position: relative;
    transition:
      border-color 180ms ease,
      transform 180ms ease,
      box-shadow 180ms ease;

    &:hover {
      border-color: rgba(183, 248, 77, 0.38);
      box-shadow: 0 20px 48px rgba(0, 0, 0, 0.32);
      transform: translateY(-3px);
    }

    &.popular {
      background:
        radial-gradient(circle at 85% 5%, rgba(183, 248, 77, 0.18), transparent 34%),
        linear-gradient(145deg, #182018, ${colors.background.lightBlack});
      border-color: rgba(183, 248, 77, 0.65);
      box-shadow: 0 18px 46px rgba(115, 164, 51, 0.16);
      padding-top: 35px;
    }

    .popular-choice {
      align-items: center;
      background: linear-gradient(90deg, #9ddb3b, #dfffaa);
      color: #17200e;
      display: flex;
      font-size: 11px;
      font-weight: 800;
      height: 35px;
      justify-content: center;
      left: 0;
      letter-spacing: 0.12em;
      position: absolute;
      top: 0;
      width: 100%;
    }

    .card-body {
      display: flex;
      flex: 1;
      flex-direction: column;
      padding: 28px 26px 16px;
    }

    .card-title {
      color: ${colors.text.white};
      font-size: 25px;
      font-weight: 700;
    }

    .card-text {
      color: ${colors.text.gray};
      min-height: 48px;
    }

    .price {
      color: ${colors.text.green};
      font-size: 30px;
      font-weight: 750;

      span {
        color: ${colors.text.gray};
        font-size: 14px;
        font-weight: 400;
      }
    }

    .notes {
      list-style: none;
      margin: 0;
      padding: 0;

      li {
        color: ${colors.text.mediumWhite};
        margin: 10px 0;
        padding-left: 22px;
        position: relative;

        &::before {
          color: ${colors.text.green};
          content: "✓";
          font-weight: 700;
          left: 0;
          position: absolute;
        }
      }
    }

    .card-footer {
      background: transparent;
      border: 0;
      padding: 18px 26px 26px;

      .btn {
        border-radius: 9px;
        font-weight: 700;
        min-height: 46px;
      }

      .btn:disabled {
        cursor: not-allowed;
        opacity: 0.45;
      }
    }
  }

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

export const InvoiceSection = styled.section`
  margin-top: 42px;
`;

export const InvoiceTableWrap = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 14px;
  margin-top: 18px;
  max-height: 60vh;
  overflow: auto;
`;

export const InvoiceTable = styled.table`
  border-collapse: collapse;
  min-width: 720px;
  width: 100%;

  th,
  td {
    border-bottom: 1px solid rgba(255, 255, 255, 0.09);
    padding: 15px 18px;
    text-align: left;
  }

  th {
    background: rgba(255, 255, 255, 0.035);
    color: ${colors.text.gray};
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  td {
    color: ${colors.text.mediumWhite};
    font-size: 13px;
  }

  tr:last-child td {
    border-bottom: 0;
  }
`;

export const InvoiceNumber = styled.div`
  color: ${colors.text.white};
  font-weight: 650;
`;

export const InvoiceStatus = styled.span<{ $status?: string | null }>`
  background: ${({ $status }) =>
    $status === "paid"
      ? "rgba(183, 248, 77, 0.12)"
      : $status === "open"
        ? "rgba(220, 102, 120, 0.14)"
        : "rgba(255, 255, 255, 0.08)"};
  border-radius: 999px;
  color: ${({ $status }) =>
    $status === "paid"
      ? colors.text.green
      : $status === "open"
        ? "#ff9eac"
        : colors.text.lightGray};
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  padding: 5px 9px;
  text-transform: capitalize;
`;

export const InvoiceActions = styled.div`
  display: flex;
  gap: 12px;

  a {
    color: ${colors.text.green};
    font-weight: 650;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

export const EmptyInvoices = styled.div`
  color: ${colors.text.gray};
  padding: 34px;
  text-align: center;
`;

export const PaymentModal = styled(Modal)`
  .modal-content {
    background: #15171b;
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 14px;
    color: ${colors.text.white};
  }

  .modal-header {
    border-bottom-color: rgba(255, 255, 255, 0.1);
  }

  .btn-close {
    filter: invert(1);
  }
`;

export const PaymentForm = styled.form`
  display: grid;
  gap: 18px;
`;

export const ModalNote = styled.p`
  color: ${colors.text.gray};
  font-size: 13px;
  margin: 0;
`;

export const ModalError = styled.div`
  background: rgba(220, 102, 120, 0.12);
  border: 1px solid rgba(220, 102, 120, 0.5);
  border-radius: 8px;
  color: #ffadba;
  font-size: 13px;
  padding: 10px 12px;
`;

export const ModalActions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

export const Loading = styled.div`
  color: ${colors.text.gray};
  padding: 60px 0;
  text-align: center;
`;

export const UpgradeSummary = styled.div`
  background: rgba(255, 255, 255, 0.035);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  margin: 0 0 24px;
  padding: 16px;
  text-align: left;
`;

export const SummaryPlanChange = styled.div`
  align-items: center;
  color: ${colors.text.gray};
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-bottom: 18px;

  strong {
    color: ${colors.text.green};
  }
`;

export const SummaryRow = styled.div`
  color: ${colors.text.gray};
  display: flex;
  font-size: 13px;
  gap: 16px;
  justify-content: space-between;
  margin: 8px 0;
`;

export const Credit = styled.span`
  color: ${colors.text.green};
`;

export const SummaryTotal = styled.div`
  align-items: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  border-top: 1px solid rgba(255, 255, 255, 0.12);
  color: ${colors.text.white};
  display: flex;
  justify-content: space-between;
  margin: 14px 0;
  padding: 13px 0;

  strong {
    color: ${colors.text.green};
    font-size: 20px;
  }
`;

export const SummaryNote = styled.p`
  color: ${colors.text.lightGray};
  font-size: 12px !important;
  line-height: 1.55;
  margin: 14px 0 0 !important;
`;
